import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAuthToken } from '@/dataProvider/axios';
import env from '@/config/env';

/**
 * Slack ↔ ncoder account link. Opened from a weave "Link ncoder account" button
 * (?slack_user=&slack_name=&slack_team=&state=). This route is protected, so the user is
 * already logged into ncoder — clicking Confirm proves ownership and binds their Slack id.
 *
 * The confirmation NAMES the Slack user on purpose: it closes the confused-deputy vector
 * (a victim tricked into opening a link for someone else's Slack id will see it isn't theirs).
 */
export const SlackLink: React.FC = () => {
  const [params] = useSearchParams();
  const slackUser = params.get('slack_user') || '';
  const slackName = params.get('slack_name') || '';
  const slackTeam = params.get('slack_team') || '';
  const state = params.get('state') || '';
  const [status, setStatus] = useState<'idle' | 'linking' | 'done' | 'error'>('idle');
  const [error, setError] = useState('');

  const who = slackName ? `${slackName} (${slackUser})` : slackUser;

  const link = async () => {
    setStatus('linking');
    try {
      const res = await fetch(`${env.API_URL}/slack/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
        body: JSON.stringify({ slack_user_id: slackUser, slack_team_id: slackTeam, state }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) setStatus('done');
      else {
        setError(data.error || `HTTP ${res.status}`);
        setStatus('error');
      }
    } catch (e) {
      setError(String(e));
      setStatus('error');
    }
  };

  if (!slackUser || !state) {
    return (
      <div style={{ maxWidth: 520, margin: '48px auto', padding: 24 }}>
        <h2>Invalid link</h2>
        <p>This link is missing parameters or has expired. Ask <b>weave</b> in Slack for a fresh link.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 520, margin: '48px auto', padding: 24, lineHeight: 1.5 }}>
      <h2>Link your Slack account to ncoder</h2>
      <p>
        You're about to link <b>Slack user {who}</b> to <b>your ncoder account</b>. After this,
        weave (in Slack) will investigate your org on your behalf.
      </p>
      <p style={{ color: '#b91c1c' }}>
        Only confirm if <b>{who}</b> is <i>your</i> Slack account.
      </p>
      {status === 'done' ? (
        <p style={{ color: '#16a34a' }}>✅ Linked! Return to Slack and ask weave anything.</p>
      ) : status === 'error' ? (
        <p style={{ color: '#b91c1c' }}>❌ {error}. The link may have expired — ask weave for a new one.</p>
      ) : (
        <button
          onClick={link}
          disabled={status === 'linking'}
          style={{ padding: '10px 18px', fontSize: 16, cursor: 'pointer' }}
        >
          {status === 'linking' ? 'Linking…' : `Link ${slackUser} to my account`}
        </button>
      )}
    </div>
  );
};

export default SlackLink;
